import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ReferralCard } from "./ReferralCard";
import {
  Landmark,
  ImagePlus,
  Save,
  X,
  MapPin,
  Mail,
  Phone,
  Building2,
  CreditCard,
  ExternalLink,
  Clock,
  Crown,
  Receipt,
  PenLine,
  Hash,
  Send,
  KeyRound,
  Trash2,
  CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Painel de configurações da Loja atual — permite editar dados e enviar/atualizar logo (papel timbrado).
 */
export const LojaSettingsView = ({ currentLoja, onUpdated }) => {
  const [form, setForm] = useState({
    name: "",
    number: "",
    oriente: "",
    obediencia: "",
    address: "",
    email: "",
    phone: "",
    meeting_schedule: "",
    logo_b64: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentLoja) {
      setForm({
        name: currentLoja.name || "",
        number: currentLoja.number || "",
        oriente: currentLoja.oriente || "",
        obediencia: currentLoja.obediencia || "",
        address: currentLoja.address || "",
        email: currentLoja.email || "",
        phone: currentLoja.phone || "",
        meeting_schedule: currentLoja.meeting_schedule || "",
        logo_b64: currentLoja.logo_b64 || null,
      });
    }
  }, [currentLoja]);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo muito grande (máx 500 KB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, logo_b64: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!currentLoja?.id) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/lojas/${currentLoja.id}`, form);
      toast.success("Dados da Loja atualizados com sucesso!");
      if (onUpdated) onUpdated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="loja-settings-view">
      <div className="luxury-card p-6 border-l-4 border-l-[#D4AF37]">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="flex items-center space-x-2 text-xs uppercase tracking-wider text-[#D4AF37] font-bold mb-1">
              <Landmark className="w-4 h-4" />
              <span>Master da Loja • Papel Timbrado & Identidade</span>
            </div>
            <h2 className="text-2xl font-serif font-bold text-white">Configurações da Loja</h2>
            <p className="text-xs text-slate-400 mt-1">
              Personalize a identidade visual e os dados que aparecem em balaústres impressos, boletins e convites.
            </p>
          </div>
          {currentLoja?.number && (
            <a
              href={`/loja/${currentLoja.number}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="loja-btn-public-page"
              className="flex items-center space-x-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(16,185,129,0.25)]"
            >
              <span>🌐</span>
              <span>Ver Página Pública</span>
            </a>
          )}
        </div>
      </div>

      <BillingCard currentLoja={currentLoja} />

      <EmailSettingsCard />

      <ReceiptConfigCard />

      <ReferralCard />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo Preview */}
        <div className="luxury-card p-6 lg:col-span-1 flex flex-col items-center">
          <div className="text-[10px] uppercase text-slate-400 tracking-wider font-bold mb-3">
            Brasão / Logo da Loja
          </div>
          <div className="w-full aspect-square bg-white rounded-2xl flex items-center justify-center overflow-hidden border-2 border-[#D4AF37]/30 mb-4 relative">
            {form.logo_b64 ? (
              <img src={form.logo_b64} alt="Brasão" className="max-w-full max-h-full object-contain p-4" data-testid="loja-logo-preview" />
            ) : (
              <div className="text-slate-300 text-center px-4">
                <Landmark className="w-12 h-12 mx-auto mb-2 opacity-40" />
                <p className="text-xs font-semibold">Sem brasão cadastrado</p>
                <p className="text-[10px] mt-1 italic">Envie um PNG para personalizar seus documentos oficiais.</p>
              </div>
            )}
          </div>

          <label className="w-full flex items-center justify-center h-11 border-2 border-dashed border-[#D4AF37]/40 rounded-xl text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 cursor-pointer text-xs font-bold uppercase tracking-wider transition-all">
            <ImagePlus className="w-4 h-4 mr-2" />
            {form.logo_b64 ? "Trocar brasão" : "Enviar brasão"}
            <input
              data-testid="loja-input-logo"
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </label>
          {form.logo_b64 && (
            <button
              data-testid="loja-btn-remove-logo"
              onClick={() => setForm({ ...form, logo_b64: null })}
              className="mt-2 text-[10px] uppercase font-bold text-rose-400 hover:text-rose-300 tracking-widest"
            >
              Remover brasão
            </button>
          )}
          <p className="text-[10px] text-slate-500 mt-3 italic text-center">
            Aparece em balaústres impressos, boletins por e-mail e no cabeçalho da barra lateral.
          </p>
        </div>

        {/* Form */}
        <div className="luxury-card p-6 lg:col-span-2 space-y-4">
          <div>
            <div className="text-[10px] uppercase text-slate-400 tracking-wider font-bold mb-2 flex items-center space-x-1">
              <Building2 className="w-3 h-3" />
              <span>Identidade da Loja</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Nome da Loja *</label>
                <input
                  data-testid="loja-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Número *</label>
                <input
                  data-testid="loja-input-number"
                  value={form.number}
                  onChange={(e) => setForm({ ...form, number: e.target.value })}
                  className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Oriente (cidade)</label>
              <input
                data-testid="loja-input-oriente"
                value={form.oriente}
                onChange={(e) => setForm({ ...form, oriente: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Obediência / Potência</label>
              <input
                data-testid="loja-input-obediencia"
                value={form.obediencia}
                onChange={(e) => setForm({ ...form, obediencia: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center space-x-1">
              <MapPin className="w-3 h-3" />
              <span>Endereço do Templo</span>
            </label>
            <input
              data-testid="loja-input-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center space-x-1">
                <Mail className="w-3 h-3" />
                <span>E-mail da Loja</span>
              </label>
              <input
                data-testid="loja-input-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center space-x-1">
                <Phone className="w-3 h-3" />
                <span>Telefone</span>
              </label>
              <input
                data-testid="loja-input-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>Dia e Horário dos Trabalhos</span>
            </label>
            <input
              data-testid="loja-input-meeting-schedule"
              value={form.meeting_schedule}
              onChange={(e) => setForm({ ...form, meeting_schedule: e.target.value })}
              placeholder="Ex.: Toda Sexta-feira às 20h00 (Ponto de Meio-Dia)"
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
            <p className="text-[10px] text-slate-500 mt-1 italic">
              Exibido no Perfil da Loja para todos os Obreiros.
            </p>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-800">
            <button
              data-testid="loja-btn-save"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] disabled:bg-slate-700 text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? "Salvando..." : "Salvar Alterações"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PLAN_INFO = {
  free: { label: "Gratuito", tone: "bg-slate-800 text-slate-300", desc: "Plano inicial." },
  pro: { label: "Pro", tone: "bg-[#D4AF37]/15 text-[#D4AF37]", desc: "Obreiros e sessões ilimitados." },
  grande_oriente: { label: "Grande Oriente", tone: "bg-purple-900/40 text-purple-300", desc: "Multi-Lojas e Dashboard do Plano Ouro." },
};

const BillingCard = ({ currentLoja }) => {
  const [loading, setLoading] = useState(false);
  const planId = currentLoja?.plan_id || "free";
  const cfg = PLAN_INFO[planId] || PLAN_INFO.free;
  const isPaid = planId !== "free";

  const openPortal = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments/billing-portal`,
        { return_url: window.location.href },
        { withCredentials: true }
      );
      if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível abrir o portal de billing.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="loja-billing-card" className="luxury-card p-6 border-l-4 border-l-[#D4AF37]">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center">
            {planId === "grande_oriente" ? <Crown className="w-5 h-5" /> : <CreditCard className="w-5 h-5" />}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">Plano da Loja</div>
            <div className="flex items-center gap-2 mt-1">
              <h3 className="text-xl font-serif font-bold text-white">{cfg.label}</h3>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${cfg.tone}`}>
                {isPaid ? "Ativo" : "Free"}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">{cfg.desc}</p>
            {currentLoja?.subscribed_at && (
              <p className="text-[10px] text-slate-500 mt-2 font-mono">
                Assinado em {new Date(currentLoja.subscribed_at).toLocaleDateString("pt-BR")}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isPaid ? (
            <button
              data-testid="loja-btn-billing-portal"
              onClick={openPortal}
              disabled={loading}
              className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] disabled:bg-slate-700 text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {loading ? "Abrindo..." : "Gerenciar Assinatura"}
            </button>
          ) : (
            <a
              data-testid="loja-btn-upgrade"
              href="/pricing"
              className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all"
            >
              <CreditCard className="w-3.5 h-3.5" />
              Fazer Upgrade
            </a>
          )}
        </div>
      </div>
    </div>
  );
};



// ==========================================
// EMAIL SETTINGS (Resend) — Master da Loja
// ==========================================
const EmailSettingsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [senderName, setSenderName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/loja/email-settings`);
      setCfg(r.data);
      setSenderEmail(r.data.sender_email || "");
      setSenderName(r.data.sender_name || "");
    } catch (err) {
      // If 403 (not master), hide the card
      setCfg({ hidden: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { sender_email: senderEmail, sender_name: senderName };
      if (newKey.trim()) payload.resend_api_key = newKey.trim();
      await axios.put(`${API}/loja/email-settings`, payload);
      toast.success("Configuração de e-mail salva com sucesso.");
      setNewKey("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar configuração de e-mail.");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm("Remover a chave da Resend?\n\nBoletins voltarão a depender da chave global da plataforma (se existir).")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/loja/email-settings/api-key`);
      toast.success("Chave removida.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover a chave.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || cfg?.hidden) return null;

  return (
    <div data-testid="email-settings-card" className="luxury-card p-6 border-l-4 border-l-sky-500">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-sky-500/15 text-sky-300 flex items-center justify-center">
            <Send className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-sky-300 font-bold">Envio de Boletins</div>
            <h3 className="text-lg font-serif font-bold text-white mt-0.5">Configuração de E-mail (Resend)</h3>
            <p className="text-[11px] text-slate-400 mt-1 max-w-lg leading-relaxed">
              Cole aqui a chave da API da <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline">Resend</a> da sua Loja para despachar os Boletins pendentes.
              Sem chave, os boletins ficam salvos como pendentes até você configurar.
            </p>
          </div>
        </div>
        <div className="shrink-0">
          {cfg?.configured ? (
            <span className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
              <CheckCircle2 className="w-3 h-3" /> Configurado
            </span>
          ) : (
            <span className="text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded-full bg-slate-700/40 text-slate-400 border border-slate-700">
              Não configurado
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center gap-1.5 mb-1.5">
            <KeyRound className="w-3 h-3" />
            Chave da API da Resend {cfg?.configured && <span className="text-slate-500 italic normal-case">(atual: {cfg.resend_api_key_masked})</span>}
          </label>
          <div className="flex gap-2">
            <input
              data-testid="email-settings-key-input"
              type="password"
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
              placeholder={cfg?.configured ? "Digite uma NOVA chave para substituir…" : "re_xxxxxxxxxxxxxxxxxxxx"}
              className="flex-1 bg-[#080C14] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-sky-400 outline-none font-mono"
            />
            {cfg?.configured && (
              <button
                data-testid="email-settings-clear-key"
                onClick={clearKey}
                disabled={saving}
                title="Remover chave"
                className="p-2 rounded-xl bg-rose-500/20 hover:bg-rose-500/40 text-rose-300 border border-rose-500/40 disabled:opacity-40"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <p className="text-[10px] text-slate-500 mt-1.5 italic">
            🔒 A chave é armazenada e enviada apenas mascarada nas consultas. Você pode substituir a qualquer momento.
          </p>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-1.5">
            Remetente (E-mail)
          </label>
          <input
            data-testid="email-settings-sender-email"
            type="email"
            value={senderEmail}
            onChange={e => setSenderEmail(e.target.value)}
            placeholder="secretaria@sualoja.org.br"
            className="w-full bg-[#080C14] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-sky-400 outline-none"
          />
          <p className="text-[10px] text-slate-500 mt-1 italic">Deve estar verificado na Resend.</p>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-1.5">
            Nome do Remetente
          </label>
          <input
            data-testid="email-settings-sender-name"
            type="text"
            value={senderName}
            onChange={e => setSenderName(e.target.value)}
            placeholder="Loja Fraternidade e Progresso"
            className="w-full bg-[#080C14] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-sky-400 outline-none"
          />
        </div>
      </div>

      <div className="flex items-center justify-end pt-4 mt-4 border-t border-slate-800">
        <button
          data-testid="email-settings-save"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:bg-slate-700 disabled:text-slate-400 text-slate-950 font-bold px-5 py-2 rounded-xl text-xs uppercase tracking-wider transition-all"
        >
          {saving ? "Salvando…" : "Salvar Configuração"}
        </button>
      </div>
    </div>
  );
};


const ReceiptConfigCard = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/treasury/receipt-config`)
      .then((r) => setCfg(r.data))
      .catch(() => toast.error("Erro ao carregar configuração de recibo."))
      .finally(() => setLoading(false));
  }, []);

  const setField = (patch) => setCfg((prev) => ({ ...(prev || {}), ...patch }));

  const handleSignatureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Assinatura muito grande (máx 500 KB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setField({ signature_b64: ev.target.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = {
        prefix: cfg.prefix || "RECIBO Nº",
        next_number: parseInt(cfg.next_number, 10) || 1,
        cnpj: cfg.cnpj || "",
        address: cfg.address || "",
        responsible_name: cfg.responsible_name || "",
        responsible_role: cfg.responsible_role || "Tesoureiro",
        signature_b64: cfg.signature_b64,
        footer_text: cfg.footer_text || "",
        show_lodge_logo: !!cfg.show_lodge_logo,
        auto_send_email: !!cfg.auto_send_email,
      };
      const res = await axios.post(`${API}/treasury/receipt-config`, payload);
      setCfg(res.data);
      toast.success("Configuração de recibo atualizada!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar configuração.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="luxury-card p-6 border-l-4 border-l-[#D4AF37]" data-testid="receipt-config-loading">
        <p className="text-xs text-slate-400">Carregando configuração de recibo…</p>
      </div>
    );
  }

  return (
    <div
      data-testid="receipt-config-card"
      className="luxury-card p-6 border-l-4 border-l-[#D4AF37] space-y-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center">
          <Receipt className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">
            Tesouraria • Configuração de Recibo
          </div>
          <h3 className="text-xl font-serif font-bold text-white">Recibo Automático</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Toda transação marcada como <strong>Paga</strong> gera um recibo com numeração sequencial, valor por
            extenso, brasão e assinatura do responsável.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center gap-1">
            <Hash className="w-3 h-3" />
            Prefixo
          </label>
          <input
            data-testid="receipt-cfg-prefix"
            value={cfg?.prefix || ""}
            onChange={(e) => setField({ prefix: e.target.value })}
            className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Próximo Nº</label>
          <input
            data-testid="receipt-cfg-next-number"
            type="number"
            min="1"
            value={cfg?.next_number || 1}
            onChange={(e) => setField({ next_number: e.target.value })}
            className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-[#D4AF37] outline-none"
          />
          <p className="text-[10px] text-slate-500 mt-1 italic">
            O próximo recibo emitido usará este número.
          </p>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">CNPJ</label>
          <input
            data-testid="receipt-cfg-cnpj"
            value={cfg?.cnpj || ""}
            onChange={(e) => setField({ cnpj: e.target.value })}
            placeholder="00.000.000/0000-00"
            className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-[#D4AF37] outline-none"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">
          Endereço no cabeçalho (opcional)
        </label>
        <input
          data-testid="receipt-cfg-address"
          value={cfg?.address || ""}
          onChange={(e) => setField({ address: e.target.value })}
          placeholder="Rua Templo Maçônico, 123, Centro"
          className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Responsável</label>
          <input
            data-testid="receipt-cfg-resp-name"
            value={cfg?.responsible_name || ""}
            onChange={(e) => setField({ responsible_name: e.target.value })}
            placeholder="Nome do Tesoureiro"
            className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Cargo</label>
          <input
            data-testid="receipt-cfg-resp-role"
            value={cfg?.responsible_role || ""}
            onChange={(e) => setField({ responsible_role: e.target.value })}
            placeholder="Tesoureiro"
            className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
          />
        </div>
      </div>

      {/* Signature */}
      <div>
        <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center gap-1">
          <PenLine className="w-3 h-3" />
          Assinatura digital (PNG transparente)
        </label>
        <div className="mt-2 flex items-center gap-4">
          <div className="w-40 h-20 bg-white rounded-lg border-2 border-slate-700 flex items-center justify-center overflow-hidden">
            {cfg?.signature_b64 ? (
              <img
                src={cfg.signature_b64}
                alt="Assinatura"
                data-testid="receipt-cfg-signature-preview"
                className="max-w-full max-h-full object-contain p-2"
              />
            ) : (
              <span className="text-[10px] text-slate-500 italic">Sem assinatura</span>
            )}
          </div>
          <div className="space-y-2">
            <label className="inline-flex items-center gap-2 border-2 border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] px-3 py-2 rounded-lg cursor-pointer text-xs font-bold uppercase tracking-wider transition-all">
              <ImagePlus className="w-3.5 h-3.5" />
              {cfg?.signature_b64 ? "Trocar assinatura" : "Enviar assinatura"}
              <input
                data-testid="receipt-cfg-signature-input"
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={handleSignatureUpload}
              />
            </label>
            {cfg?.signature_b64 && (
              <button
                onClick={() => setField({ signature_b64: null })}
                className="block text-[10px] uppercase font-bold text-rose-400 hover:text-rose-300 tracking-widest"
              >
                Remover assinatura
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Rodapé livre (opcional)</label>
        <textarea
          data-testid="receipt-cfg-footer"
          rows={2}
          value={cfg?.footer_text || ""}
          onChange={(e) => setField({ footer_text: e.target.value })}
          placeholder="Ex: Recibo válido como comprovante fiscal para prestação de contas maçônicas."
          className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
        />
      </div>

      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
        <input
          data-testid="receipt-cfg-show-logo"
          type="checkbox"
          checked={!!cfg?.show_lodge_logo}
          onChange={(e) => setField({ show_lodge_logo: e.target.checked })}
          className="w-4 h-4 accent-[#D4AF37]"
        />
        Exibir brasão da Loja no cabeçalho do recibo
      </label>

      <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer">
        <input
          data-testid="receipt-cfg-auto-send"
          type="checkbox"
          checked={!!cfg?.auto_send_email}
          onChange={(e) => setField({ auto_send_email: e.target.checked })}
          className="mt-0.5 w-4 h-4 accent-[#D4AF37]"
        />
        <span>
          <span className="font-semibold text-white">Enviar recibo por e-mail automaticamente</span>
          <span className="block text-[10px] text-slate-500 mt-0.5">
            Assim que uma transação é marcada como paga, o recibo é enviado ao e-mail cadastrado do Irmão ou do
            Cliente/Fornecedor. A assinatura acima (do Tesoureiro) vai no rodapé.
          </span>
        </span>
      </label>

      <div className="flex justify-end pt-3 border-t border-slate-800">
        <button
          data-testid="receipt-cfg-save"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] disabled:bg-slate-700 text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
        >
          <Save className="w-4 h-4" />
          {saving ? "Salvando..." : "Salvar Configuração"}
        </button>
      </div>
    </div>
  );
};
